/* eslint-disable jsx-a11y/click-events-have-key-events */

'use client';

import { amplitudeSendEvent, getScreenName } from '@musinsa/frontend-utils';
import { Button, Padding, Typography } from '@musinsa/mds';
import { Tooltip } from '@musinsa/mds-ui';
import { SERVICE, useDeviceInfo } from '@musinsa/packages-device-detection';
import { useQuery } from '@tanstack/react-query';
import { useCallback } from 'react';
import { twMerge } from 'tailwind-merge';
import { FloatingButtonGroup, ScrollToTopButton } from '../common/components';
import { curatorQueries } from '../query-factory';
import { DraggableSelectedSnapList } from './DraggableSelectedSnapList';
import type { ShopSnapCurationEditFormData } from './type';

interface Props {
  selectedSnaps: ShopSnapCurationEditFormData['selectedSnaps'];
  onUnselect: (snapId: string) => void;
  onSubmit: () => void;
  onDragEnd?: (items: ShopSnapCurationEditFormData['selectedSnaps']) => void;
  className?: string;
}

export function SelectedSnapsWithButton({
  selectedSnaps,
  onUnselect,
  onSubmit,
  onDragEnd,
  className,
}: Props) {
  const { service } = useDeviceInfo();
  const { data: userInfo } = useQuery(curatorQueries.profile.userInfo);
  const curatorId = userInfo?.snsTitle ?? '';

  const handleDragEnd = useCallback(
    (items: ShopSnapCurationEditFormData['selectedSnaps']) => {
      onDragEnd?.(items);
    },
    [onDragEnd],
  );

  const handleSubmit = useCallback(() => {
    if (service === SERVICE.MUSINSA && curatorId) {
      amplitudeSendEvent({
        action_name: 'click',
        button_name: 'snap_add_complete',
        component_name: 'button',
        event_name: 'click_button',
        from: getScreenName(),
        id: curatorId,
        section_name: 'curator_curation_create',
        type: 'snap',
      });
    }
    onSubmit();
  }, [curatorId, service, onSubmit]);

  return (
    <div
      className={twMerge(
        'fixed bottom-0 left-0 right-0 z-50 max-w-[600px] mx-auto',
        'bg-white',
        className,
      )}
      role="button"
      tabIndex={0}
      onClick={(e) => {
        e.stopPropagation();
        e.preventDefault();
      }}
    >
      {selectedSnaps.length > 1 && (
        <Tooltip
          className="!absolute left-[80px] -top-[25px] !max-w-none"
          align="top left"
          content="스냅을 꾹 눌러서 순서를 변경할 수 있습니다."
          duration={5000}
          maxCount={1}
        />
      )}

      <FloatingButtonGroup.Container
        className="absolute -top-12 right-0"
        style={{
          // NOTE: translate-y-full 클래스가 적용되지 않음
          transform: 'translateY(-100%)',
        }}
      >
        <ScrollToTopButton />
      </FloatingButtonGroup.Container>

      <DraggableSelectedSnapList
        selectedSnaps={selectedSnaps}
        onUnselect={onUnselect}
        onDragEnd={handleDragEnd}
      />

      <div
        className="relative bg-white safe-area-bottom"
        style={{ borderTop: '1px solid rgba(138, 138, 138, 0.1)' }}
      >
        <Padding x="4" y="3">
          <Button className="w-full" onClick={handleSubmit}>
            <Typography variant="body14pxReg">
              {selectedSnaps.length > 0 ? `${selectedSnaps.length}개` : ''} 선택 완료하기
            </Typography>
          </Button>
        </Padding>
      </div>
    </div>
  );
}
